import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

/**
 * Dynamic per-card dimensions. The rival card is a tall "mascot" card: a large uncropped
 * avatar sitting in a glow panel, with the name + coin balance below. Sizes scale with the
 * grid's cardWidth so 2-up on phones and denser grids on web both look right.
 */
export const getRivalCardDimensions = (cardWidth: number) => {
  const imageHeight = Math.round(Math.max(120, Math.min(cardWidth * 0.92, 190)));
  const avatarSize = Math.round(imageHeight * 0.98);
  return { imageHeight, avatarSize };
};

/** Info block below the image: paddingTop 9 + name 20 + gap 6 + metaRow 18 + paddingBottom 12. */
const INFO_HEIGHT = 65;
const NAME_LINE_HEIGHT = 20;
const META_ROW_HEIGHT = 18;
const CARD_BORDER_WIDTH = 2;

/**
 * Total outer height of a rival card. The card is pinned to this instead of hugging its content so
 * every card in a row is identical, and so the grid's Invite tile can match it exactly
 * (see InviteCard `height`) rather than falling back to its own aspect ratio.
 */
export const getRivalCardHeight = (cardWidth: number) =>
  getRivalCardDimensions(cardWidth).imageHeight + INFO_HEIGHT + 2 * CARD_BORDER_WIDTH;

export const getRivalCardStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      width: '100%',
      backgroundColor: theme.background.card,
      borderRadius: theme.borderRadius.lg,
      borderWidth: CARD_BORDER_WIDTH,
      borderColor: 'transparent',
      overflow: 'hidden',
    },
    cardSelected: {
      borderColor: theme.primary,
    },
    cardDisabled: {
      opacity: 0.42,
    },
    imageArea: {
      width: '100%',
      // A faint purple wash stands in for the design's radial glow (no gradient dep).
      backgroundColor: theme.primary + '14',
      alignItems: 'center',
      justifyContent: 'flex-end',
      overflow: 'hidden',
    },
    selectedBadge: {
      position: 'absolute',
      top: 8,
      right: 8,
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: theme.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    selectedBadgeText: {
      fontSize: 13,
      fontWeight: '800',
      color: theme.onPrimary,
    },
    info: {
      paddingHorizontal: 12,
      paddingTop: 9,
      paddingBottom: 12,
      gap: 6,
    },
    name: {
      fontSize: Platform.OS === 'web' ? 15 : 14,
      lineHeight: NAME_LINE_HEIGHT,
      fontWeight: '700',
      color: theme.black,
      textAlign: 'left',
    },
    // Name → [record | coins] row (design 2a): W/D/L tiles on the left, coin balance on the right.
    metaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: META_ROW_HEIGHT,
      gap: 6,
    },
    recordRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      flexShrink: 1,
    },
    recTile: {
      height: 16,
      minWidth: 16,
      paddingHorizontal: 4,
      borderRadius: 4,
      alignItems: 'center',
      justifyContent: 'center',
    },
    recTileText: {
      fontSize: 9.5,
      fontWeight: '800',
      color: '#111827',
      fontVariant: ['tabular-nums'],
    },
    recWin: { backgroundColor: '#6EE7B7' },
    recDraw: { backgroundColor: '#9CA3AF' },
    recLoss: { backgroundColor: '#F87171' },
    noHistory: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.gray[500],
      flexShrink: 1,
    },
    coinsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      flexShrink: 0,
    },
    coinIcon: {
      width: 13,
      height: 13,
    },
    coinsText: {
      fontSize: 12,
      fontWeight: '700',
      color: theme.accent,
      fontVariant: ['tabular-nums'],
    },
  });
